import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * TabsScrollSlider — a draggable thumb-on-track control that scrolls
 * an overflow-x container (e.g. the Stock Modal's TabsList). Centered,
 * desktop-only (hidden on `<sm`). Designed for cases where there are
 * too many tabs to fit at typical desktop widths but a normal
 * scrollbar feels clunky.
 *
 * Behavior:
 *   - Track width: a narrow centered strip above the tabs.
 *   - Thumb position mirrors the container's scroll position.
 *   - Drag the thumb → container scrolls proportionally.
 *   - Click anywhere on the track → thumb jumps to that point.
 *   - Native scroll of the container (wheel/touch/swipe) syncs the
 *     thumb position back via the container's `scroll` event.
 *   - Renders nothing when the container fits without overflow (no
 *     visual noise when there's nothing to scroll).
 *
 * Props:
 *   targetRef:  React ref to the scrollable container (the TabsList).
 *   className:  Optional wrapper class overrides.
 */
export default function TabsScrollSlider({ targetRef, className = '' }) {
  const trackRef = useRef(null);
  const [scrollPct, setScrollPct] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);

  // Read the container's scroll geometry and recompute thumb position.
  // Called on mount, on container scroll, on window resize, and via a
  // ResizeObserver on the container so tab counts that change (e.g.
  // "My Position" appearing when you click into a stock you own) get
  // picked up without a remount.
  const recompute = useCallback(() => {
    const el = targetRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 1) {
      setHasOverflow(false);
      setScrollPct(0);
      return;
    }
    setHasOverflow(true);
    setScrollPct(Math.max(0, Math.min(100, (el.scrollLeft / max) * 100)));
  }, [targetRef]);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    recompute();
    el.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', recompute);
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(recompute)
      : null;
    if (ro) ro.observe(el);
    return () => {
      el.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
      if (ro) ro.disconnect();
    };
  }, [targetRef, recompute]);

  // Scroll the container to a given pct of its scroll range.
  const scrollToPct = useCallback((pct) => {
    const el = targetRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 1) return;
    const clamped = Math.max(0, Math.min(100, pct));
    el.scrollLeft = (clamped / 100) * max;
  }, [targetRef]);

  const startDrag = (e) => {
    e.preventDefault();
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();

    const handleMove = (ev) => {
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const x = clientX - rect.left;
      const pct = (x / rect.width) * 100;
      scrollToPct(pct);
    };
    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleUp);
    // Move immediately on the initial click as well — feels more
    // responsive than waiting for the first mousemove tick.
    handleMove(e.nativeEvent);
  };

  // Nudge the container by ~one tab's width per chevron click.
  // Approximated as 25% of visible viewport width; feels right
  // whether you have 8 tabs or 14.
  const nudge = useCallback((direction) => {
    const el = targetRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.25 * direction;
    el.scrollBy({ left: step, behavior: 'smooth' });
  }, [targetRef]);

  if (!hasOverflow) return null;

  // Fuchsia palette — light fuchsia for dark theme, dark fuchsia for
  // light theme — per Round 92o++++++++N++++++++ user request. Track
  // is a translucent fuchsia; thumb is a saturated fuchsia; triangles
  // match the thumb tone so the whole control reads as one piece.
  const trackBg = 'bg-fuchsia-700 dark:bg-fuchsia-300/60';
  const thumbBg = 'bg-fuchsia-900 hover:bg-fuchsia-950 dark:bg-fuchsia-200 dark:hover:bg-fuchsia-100';
  const triBg = 'text-fuchsia-900 dark:text-fuchsia-200 hover:text-fuchsia-950 dark:hover:text-fuchsia-100';

  return (
    <div
      className={`hidden sm:flex items-center justify-center gap-0 px-4 pt-2 pb-1 select-none ${className}`}
      data-testid="tabs-scroll-slider"
    >
      {/* Left chevron — sits ON the track edge with slight overlap. */}
      <button
        type="button"
        onClick={() => nudge(-1)}
        className={`relative z-10 -mr-1.5 ${triBg} transition-colors`}
        title="Scroll tabs left"
        aria-label="Scroll tabs left"
        data-testid="tabs-scroll-slider-left"
      >
        {/* Pure-CSS triangle — sized to overlap the bar end by ~3px. */}
        <span
          className="block w-0 h-0"
          style={{
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent',
            borderRight: '7px solid currentColor',
          }}
        />
      </button>

      {/* Track — width matches the "LIVE" pill at the modal's top-left. */}
      <div
        ref={trackRef}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        className={`relative h-1.5 w-[110px] rounded-full cursor-pointer ${trackBg} ring-1 ring-fuchsia-500/30 dark:ring-fuchsia-400/30`}
        role="slider"
        aria-label="Scroll modal tabs"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(scrollPct)}
      >
        <div
          className={`absolute top-1/2 -translate-y-1/2 h-2.5 rounded-full shadow-sm transition-colors ${thumbBg}`}
          style={{
            width: `${Math.max(24, computeThumbPct(targetRef))}%`,
            left: `calc(${scrollPct}% - ${(scrollPct / 100) * Math.max(24, computeThumbPct(targetRef))}%)`,
          }}
          data-testid="tabs-scroll-slider-thumb"
        />
      </div>

      {/* Right chevron — mirrors the left one. */}
      <button
        type="button"
        onClick={() => nudge(1)}
        className={`relative z-10 -ml-1.5 ${triBg} transition-colors`}
        title="Scroll tabs right"
        aria-label="Scroll tabs right"
        data-testid="tabs-scroll-slider-right"
      >
        <span
          className="block w-0 h-0"
          style={{
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent',
            borderLeft: '7px solid currentColor',
          }}
        />
      </button>
    </div>
  );
}

// Helper: compute thumb width as the % of container visible per scroll
// range. Mirrors how a real OS scrollbar sizes its thumb.
function computeThumbPct(targetRef) {
  const el = targetRef.current;
  if (!el || el.scrollWidth === 0) return 30;
  return (el.clientWidth / el.scrollWidth) * 100;
}
